/**
 * useOverlayUrlSync Hook
 *
 * Automatically syncs overlay URLs when media uploads complete.
 * Subscribes to MediaUrlUpdateService and updates overlay `src` properties
 * when blob URLs are replaced with Convex URLs.
 *
 * The hook:
 * 1. Listens for URL update events from MediaUrlUpdateService
 * 2. Preloads the new Convex URLs into browser cache for smooth transition
 * 3. Updates all overlays that reference the old blob URL
 * 4. Prevents video playback interruption during the URL switch
 */

import { useEffect, useCallback, useRef } from 'react';
import { Overlay, ClipOverlay, SoundOverlay, ImageOverlay } from '../types';
import { MediaUrlUpdateService, UrlUpdateEvent } from '../services/media-url-update-service';
import { preloadVideoUrl } from '../utils/general/video-preloader';
import { cleanupBlobAfterConvexUpload, getMediaItem } from '../utils/general/indexdb';

interface UseOverlayUrlSyncOptions {
  /** Whether to preload Convex URLs before switching (recommended for smooth transition) */
  enablePreloading?: boolean;
  /** Timeout for preloading in milliseconds */
  preloadTimeout?: number;
}

/**
 * Hook to automatically sync overlay URLs when uploads complete
 *
 * @param overlays - Current array of overlays
 * @param changeOverlay - Function to update an overlay's properties
 * @param options - Configuration options
 */
export const useOverlayUrlSync = (
  overlays: Overlay[],
  changeOverlay: (id: number, updater: Partial<Overlay> | ((overlay: Overlay) => Overlay)) => void,
  options: UseOverlayUrlSyncOptions = {}
): void => {
  const { enablePreloading = true, preloadTimeout = 5000 } = options;

  // Use refs to avoid stale closures in the subscription callback
  const overlaysRef = useRef(overlays);
  const changeOverlayRef = useRef(changeOverlay);

  // Keep refs updated
  useEffect(() => {
    overlaysRef.current = overlays;
  }, [overlays]);

  useEffect(() => {
    changeOverlayRef.current = changeOverlay;
  }, [changeOverlay]);

  // Handle URL update events
  const handleUrlUpdates = useCallback(
    async (updates: UrlUpdateEvent[]) => {
      const currentOverlays = overlaysRef.current;
      const currentChangeOverlay = changeOverlayRef.current;

      for (const { fileId, oldUrl, newUrl, fileName } of updates) {
        console.log(
          `[UrlSync] Processing URL update for ${fileName || fileId}:`,
          `\n  Old URL: ${oldUrl.substring(0, 50)}...`,
          `\n  New URL: ${newUrl.substring(0, 50)}...`
        );

        // Find overlays using this old URL
        const affectedOverlays = currentOverlays.filter(
          (overlay) => 'src' in overlay && overlay.src === oldUrl
        );

        if (affectedOverlays.length === 0) {
          console.log(`[UrlSync] No overlays found using old URL, skipping`);
          continue;
        }

        console.log(`[UrlSync] Found ${affectedOverlays.length} overlay(s) using old URL`);

        // Preload the new URL before switching (for smooth transition)
        if (enablePreloading) {
          console.log(`[UrlSync] Preloading Convex URL into browser cache...`);
          try {
            await preloadVideoUrl(newUrl, preloadTimeout);
            console.log(`[UrlSync] ✓ Convex URL preloaded successfully`);
          } catch (error) {
            console.warn(`[UrlSync] ⚠ Preload failed, switching anyway:`, error);
          }
        }

        // Get media item metadata from IndexedDB to store on overlay
        const mediaItem = await getMediaItem(fileId);
        const mediaMetadata: Partial<ClipOverlay | SoundOverlay | ImageOverlay> = {
          src: newUrl,
        };

        // Add storage tracking metadata if available
        if (mediaItem) {
          if (mediaItem.convexStorageId) {
            mediaMetadata.mediaStorageId = mediaItem.convexStorageId;
          }
          if (mediaItem.fileHash) {
            mediaMetadata.mediaFileHash = mediaItem.fileHash;
          }
          if (mediaItem.name) {
            mediaMetadata.mediaFileName = mediaItem.name;
          }
          console.log(
            `[UrlSync] Adding media metadata: storageId=${mediaItem.convexStorageId?.substring(0, 16)}..., fileHash=${mediaItem.fileHash?.substring(0, 16)}...`
          );
        }

        // Update all affected overlays with URL and metadata
        for (const overlay of affectedOverlays) {
          console.log(`[UrlSync] Updating overlay ${overlay.id} from blob to Convex URL with metadata`);
          currentChangeOverlay(overlay.id, mediaMetadata as Partial<Overlay>);
        }

        console.log(
          `[UrlSync] ✓ URL sync complete for ${fileName || fileId}: ${affectedOverlays.length} overlay(s) updated`
        );

        // Clean up blob URL after a short delay to ensure all components have processed the update
        // This revokes the blob URL and removes blob data from IndexedDB to free memory
        setTimeout(async () => {
          console.log(`[UrlSync] Cleaning up blob URL for ${fileName || fileId}...`);
          await cleanupBlobAfterConvexUpload(fileId);
        }, 500); // 500ms delay to ensure smooth transition
      }
    },
    [enablePreloading, preloadTimeout]
  );

  // Subscribe to URL update events
  useEffect(() => {
    console.log(`[UrlSync] Subscribing to MediaUrlUpdateService`);

    const unsubscribe = MediaUrlUpdateService.subscribe(handleUrlUpdates);

    return () => {
      console.log(`[UrlSync] Unsubscribing from MediaUrlUpdateService`);
      unsubscribe();
    };
  }, [handleUrlUpdates]);
};

/**
 * Hook to get the current upload sync status for debugging/UI
 */
export const useUrlSyncStatus = () => {
  const listenerCount = MediaUrlUpdateService.getListenerCount();

  return {
    isListening: listenerCount > 0,
    listenerCount,
  };
};
